import { DialogComponent } from '@theme/dialog';

/**
 * Opens an announcement's content in a modal dialog instead of navigating.
 *
 * Two modes:
 * - default: fetches the linked same-origin page and shows its main content
 *   (handy for policy pages — shipping, refund, … — so the shopper keeps their
 *   place). Progressive enhancement: the trigger stays a real `<a href>`, and
 *   cross-origin links, modified clicks and fetch failures fall back to
 *   normal navigation.
 * - `data-mode="blocks"`: the dialog already contains theme blocks (e.g. an
 *   email signup), so a click just opens it — nothing is fetched.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement} content – Dialog body: fetched markup is injected here (default mode);
 *   in blocks mode it already holds the theme blocks.
 * @property {HTMLHeadingElement} [title] – Heading shown above the fetched content (default mode).
 *
 * @extends DialogComponent<Refs>
 */
class PopupLinkDialog extends DialogComponent {
  requiredRefs = ['dialog', 'content'];

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {boolean} */
  #loaded = false;

  connectedCallback() {
    super.connectedCallback?.();
    if (this.dataset.autoShow === 'true') {
      this.#scheduleAutoShow();
    }
    this.#setupCloseListener();
  }

  #setupCloseListener() {
    const closeButton = this.querySelector('[on\\:click="/closeDialog"]');
    if (!closeButton) return;

    closeButton.addEventListener('click', () => {
      this.#setSuppression();
    });
  }

  #scheduleAutoShow() {
    if (!this.#shouldAutoShow()) return;

    const delay = parseInt(this.dataset.autoShowDelay || '0', 10) * 1000;
    setTimeout(() => {
      if (this.#shouldAutoShow()) {
        this.showDialog();
      }
    }, delay);
  }

  #shouldAutoShow() {
    if (!this.#isSuppressionActive()) {
      return this.#matchesPageType();
    }
    return false;
  }

  #isSuppressionActive() {
    const suppressionKey = `popup-${this.#getPopupId()}-suppressed-until`;
    const suppressedUntil = localStorage.getItem(suppressionKey);

    if (!suppressedUntil) return false;

    const suppressedDate = new Date(suppressedUntil);
    const now = new Date();
    return now < suppressedDate;
  }

  #matchesPageType() {
    const pageType = this.#detectPageType();
    const settingKey = `data-auto-show-on-${pageType}`;

    return this.dataset[this.#camelCase(settingKey.replace('data-', ''))] === 'true';
  }

  #detectPageType() {
    const url = window.location.pathname.toLowerCase().replace(/\/$/, '');

    if (!url || url === '') return 'homepage';
    if (url.startsWith('/collections') && !url.includes('/products')) return 'collections';
    if (url.startsWith('/products')) return 'product-pages';

    const systemPaths = ['/search', '/cart', '/checkout', '/account', '/orders', '/admin'];
    if (systemPaths.some((path) => url.startsWith(path))) return 'others';

    return 'custom-pages';
  }

  #camelCase(str) {
    return str.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
  }

  #getPopupId() {
    return this.dataset.blockId || 'default';
  }

  /**
   * Handles a click on the trigger link.
   * @param {MouseEvent} event
   */
  open = async (event) => {
    // Let the browser handle new tab / new window / download intents.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    // Blocks mode: the dialog's content is already in the DOM — just open it.
    if (this.dataset.mode === 'blocks') {
      event.preventDefault();
      this.showDialog();
      return;
    }

    const link = /** @type {Element} */ (event.target).closest('a[href]');
    if (!(link instanceof HTMLAnchorElement)) return;

    const url = new URL(link.href, window.location.href);

    // Only same-origin pages can be fetched and shown inline.
    if (url.origin !== window.location.origin) return;

    event.preventDefault();

    if (!this.#loaded) {
      const loaded = await this.#loadContent(url, link.textContent ?? '');
      if (!loaded) {
        window.location.assign(url.href);
        return;
      }
      this.#loaded = true;
    }

    this.showDialog();
  };

  /**
   * Fetches the target page and injects its main content into the dialog.
   * @param {URL} url
   * @param {string} fallbackTitle – Trigger link text, used if the page has no heading.
   * @returns {Promise<boolean>} Whether content was loaded.
   */
  async #loadContent(url, fallbackTitle) {
    const { content } = this.refs;
    if (!content) return false;

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(url.href, { signal: this.#abortController.signal });
      if (!response.ok) return false;

      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const main = doc.querySelector('#MainContent') ?? doc.querySelector('main');
      if (!main) return false;

      const title =
        main.querySelector('h1')?.textContent?.trim() ||
        stripSiteName(doc.title) ||
        fallbackTitle.trim();

      content.innerHTML = main.innerHTML;
      content.scrollTop = 0;

      // The page often leads with its own title heading — drop it so it isn't
      // shown twice above our own.
      if (title) {
        const heading = content.querySelector('h1, h2');
        if (heading?.textContent?.trim() === title) {
          (heading.closest('.shopify-policy__title') ?? heading).remove();
        }
      }

      this.#setTitle(title);
      return true;
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return false;
      console.error('[popup-link-dialog] Failed to load', url.href, error);
      return false;
    }
  }

  /**
   * Populates (or hides) the dialog's title heading.
   * @param {string} title
   */
  #setTitle(title) {
    const { title: heading, dialog } = this.refs;
    if (!heading) return;

    if (title) {
      heading.textContent = title;
      heading.hidden = false;
      if (heading.id) {
        dialog.setAttribute('aria-labelledby', heading.id);
        dialog.removeAttribute('aria-label');
      }
    } else {
      heading.hidden = true;
    }
  }

  #setSuppression() {
    if (this.dataset.autoShow !== 'true') return;

    const suppressionDays = parseInt(this.dataset.autoShowSuppressionDays || '1', 10);
    const suppressedUntil = new Date();
    suppressedUntil.setDate(suppressedUntil.getDate() + suppressionDays);

    const suppressionKey = `popup-${this.#getPopupId()}-suppressed-until`;
    localStorage.setItem(suppressionKey, suppressedUntil.toISOString());
  }
}

/**
 * Trims a trailing " – Shop name" / " | Shop name" segment from a page title.
 * @param {string} pageTitle
 * @returns {string}
 */
function stripSiteName(pageTitle) {
  if (!pageTitle) return '';
  return pageTitle.split(/\s+[–—|]\s+/)[0].trim();
}

if (!customElements.get('popup-link-dialog')) {
  customElements.define('popup-link-dialog', PopupLinkDialog);
}
